





















































import elementResizeDetectorMaker from 'element-resize-detector';
import debounce from 'debounce';
import type { EditorContent, MergeValues, EBlockWidth } from '@getaccept/editor-lib';
import { NodeType, EditorHelper, Context, ContractHelper } from '@getaccept/editor-lib';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { EditorRecipientInput } from '@getaccept/lib-shared/src/types/editor-recipient-input';
import type { SigningTheme } from '@getaccept/lib-shared/src/signing-theme/types/theme';
import type { Font, FontSettings } from '@getaccept/lib-shared/src/fonts/types/font';
import { openSansV29 } from '@getaccept/lib-shared/src/fonts/font-faces';
import { FontService } from '@getaccept/lib-shared/src/fonts/font-service';
import type { PartiallySignedEvent } from '@getaccept/editor-lib/src/types/signed-field';
import type { PropType, Ref } from '@vue/composition-api';
import {
  nextTick,
  defineComponent,
  computed,
  ref,
  watch,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api';
import type { PusherClient } from '@getaccept/pusher';
import type { Comment } from '@getaccept/lib-shared/src/contextual-commenting/types/comment';
import { CommentsHelper } from '@getaccept/lib-shared/src/contextual-commenting/helpers/comments.helper';
import { CreateContextualCommentButtonState } from '@getaccept/lib-shared/src/contextual-commenting/types/create-contextual-comment-button-state';
import DetachedCommentAnchor from '@getaccept/lib-shared/src/contextual-commenting/components/DetachedCommentAnchor.vue';
import { EditorViewInputAction, namespace } from '../store/editor-view-input.constants';
import { editorViewStore } from '../store/editor-view.store';
import { editorViewInputState } from '../store/editor-view-input.store';
import { EditorViewAction } from '../store/editor-view.constants';
import EditorSection from './EditorSection.vue';

export default defineComponent({
  name: 'EditorBlockView',
  components: {
    DetachedCommentAnchor,
    EditorSection,
  },
  props: {
    authToken: { type: Object as PropType<{ jwt: string; exp: number }>, default: undefined },
    isPreview: { type: Boolean },
    documentId: { type: String, default: undefined },
    authId: { type: String, default: undefined },
    content: { type: Object as PropType<EditorContent>, default: undefined },
    focusedRequiredId: { type: String, default: undefined },
    mergeValues: { type: Object as PropType<MergeValues>, default: undefined },
    isPdfViewer: { type: Boolean, default: false },
    isDsr: { type: Boolean, default: false },
    isBlockToggled: { type: Boolean, default: false },
    isSigningSite: { type: Boolean, default: false },
    blockId: { type: String, default: undefined },
    sender: { type: Object as PropType<User>, default: undefined },
    theme: { type: Object as PropType<SigningTheme>, default: undefined },
    recipient: { type: Object as PropType<Recipient>, default: undefined as Recipient },
    recipientInput: { type: Array as PropType<EditorRecipientInput[]>, default: () => [] },
    context: { type: String as PropType<Context>, default: () => Context.Document },
    partiallySignedFields: { type: Array as PropType<PartiallySignedEvent[]>, default: () => [] },
    pusherClient: { type: Object as PropType<PusherClient>, default: undefined },
    comments: { type: Array as PropType<Comment[]>, default: () => [] },
    selectedCommentId: { type: String, default: '' },
    showContextualComments: { type: Boolean, default: false },
    createContextualCommentButtonState: {
      type: String as PropType<CreateContextualCommentButtonState>,
      default: CreateContextualCommentButtonState.Enabled,
    },
  },
  setup(props, { emit }) {
    const editorContainer: Ref<HTMLElement> = ref(null);
    const blockWidthClass: Ref<EBlockWidth> = ref(null);
    const blockOffsetWidth = ref(0);
    const elementResizeDetector = ref(
      elementResizeDetectorMaker({
        strategy: 'scroll',
      })
    );

    const documentStatusSigned = computed(() => editorViewStore?.state.documentStatusSigned);
    const documentRecalled = computed(() => editorViewStore?.state.documentRecalled);
    const previewTokenExpired = computed(() => editorViewStore?.state.previewTokenExpired);
    const updatingInputs = computed(() => editorViewInputState?.updatingInputs || []);
    const failedInputUpdates = computed(() => editorViewInputState?.failedInputUpdates || []);
    const alert = computed(() => editorViewInputState?.alert);
    const sections = computed(() => props.content?.sections);
    const filteredSections = computed(() =>
      ContractHelper.filterSectionsByContractLinkAccess(sections.value, props.recipient)
    );
    const visibleSections = computed(() =>
      props.context === Context.DsrTemplate
        ? filteredSections.value
        : filteredSections.value.filter(({ hidden }) => !hidden)
    );

    const firstRowContainsCoverImage = computed(() => {
      if (!sections.value?.[0]) {
        return false;
      }
      return EditorHelper.rowContainsCoverSize(sections.value[0].rows[0], NodeType.Image);
    });

    const lastRowContainsCoverImage = computed(() => {
      if (!sections.value?.[0]) {
        return false;
      }
      const { rows } = sections.value[0];
      return EditorHelper.rowContainsCoverSize(rows[rows.length - 1], NodeType.Image);
    });

    const sectionIds = computed(() => sections.value.map(section => section.id));

    const commentsWithoutSectionId = computed(() =>
      props.comments.filter(
        comment => !sectionIds.value.includes(comment.editorSelection?.sectionId)
      )
    );

    const loadFont = async (font: Font, cssVariable: string) => {
      FontService.loadFont(font);
      await nextTick();
      editorContainer.value?.style?.setProperty(cssVariable, font.fontFamily);
    };

    watch(alert, () => {
      if (!alert.value) {
        return;
      }

      emit('alert', alert.value);
      editorViewStore.dispatch(`${namespace}/${EditorViewInputAction.SetAlert}`, null);
    });

    watch(
      () => props.content.fontSettings,
      (fonts: FontSettings) => {
        if (
          EditorHelper.shouldLoadFont(
            fonts?.defaultFont,
            props.isSigningSite,
            props.isPreview,
            props.theme?.fontSettings?.defaultFont
          )
        ) {
          loadFont(
            fonts?.defaultFont || props.theme.fontSettings.defaultFont,
            '--editor-default-font-family'
          );
        }
        if (
          EditorHelper.shouldLoadFont(
            fonts?.headingsFont,
            props.isSigningSite,
            props.isPreview,
            props.theme?.fontSettings?.headingsFont
          )
        ) {
          loadFont(
            fonts?.headingsFont || props.theme.fontSettings.headingsFont,
            '--editor-headings-font-family'
          );
        }
      },
      { immediate: true }
    );

    watch(
      () => props.authToken,
      () => {
        editorViewStore.dispatch(EditorViewAction.SetToken, props.authToken);

        if (
          props.authToken?.jwt === editorViewStore.state.authToken?.jwt &&
          props.authToken?.exp === editorViewStore.state.authToken?.exp
        ) {
          editorViewStore.dispatch(EditorViewAction.SetTokenIsSynced, true);
        }
      },
      { immediate: true }
    );

    watch(
      () => props.isPreview,
      () => {
        editorViewStore.dispatch(EditorViewAction.SetIsPreview, props.isPreview);
      },
      { immediate: true }
    );

    watch(
      () => props.documentId,
      () => {
        editorViewStore.dispatch(EditorViewAction.SetDocumentId, props.documentId);
      },
      { immediate: true }
    );

    watch(
      () => props.recipient,
      () => {
        if (props.recipient) {
          editorViewStore.dispatch(EditorViewAction.SetRecipientId, props.recipient.id);
        }
      },
      { immediate: true }
    );

    watch(
      () => props.authId,
      () => {
        editorViewStore.dispatch(EditorViewAction.SetAuthId, props.authId);
      },
      { immediate: true }
    );

    watch(
      () => editorViewStore.state.tokenIsSynced,
      tokenIsSynced => {
        if (!tokenIsSynced) {
          emit('update-token', editorViewStore.state.authToken);
        }
      }
    );

    watch(documentStatusSigned, () => {
      if (documentStatusSigned.value) {
        emit('set-document-status-signed');
        editorViewStore.dispatch(EditorViewAction.EmitSetDocumentStatusSigned, null);
      }
    });

    watch(documentRecalled, () => {
      if (documentRecalled.value) {
        emit('set-document-recalled');
        editorViewStore.dispatch(EditorViewAction.EmitDocumentRecalled, null);
      }
    });

    watch(previewTokenExpired, () => {
      if (previewTokenExpired.value) {
        emit('set-preview-token-expired');
        editorViewStore.dispatch(EditorViewAction.EmitPreviewTokenExpired, null);
      }
    });

    const initFont = () => {
      if (props.content.fontSettings?.defaultFont) {
        return;
      } else if (!props.isSigningSite && !props.theme?.fontSettings?.defaultFont) {
        loadFont(openSansV29, '--editor-default-font-family');
      }
    };

    const setBlockWidth = (width: number) => {
      if (EditorHelper.isSlideShowLightboxVisible()) {
        return;
      }
      const blockWidth = EditorHelper.getBlockWidth(width);
      blockWidthClass.value = EditorHelper.getBlockWidthClass(blockWidth);
    };

    const updateBlockWidth = () => {
      blockOffsetWidth.value = editorContainer.value?.offsetWidth;
      setBlockWidth(blockOffsetWidth.value);
    };

    const initResizeDetector = () => {
      if (!editorContainer.value) {
        return;
      }
      elementResizeDetector.value.listenTo(editorContainer.value, handleBlockResize);
    };

    const debounceUpdateBlockWidth = debounce(updateBlockWidth, 200);

    const handleBlockResize = ({ offsetWidth }: HTMLDivElement) => {
      if (blockOffsetWidth.value === offsetWidth) {
        return;
      }
      debounceUpdateBlockWidth();
    };

    onMounted(async () => {
      await nextTick();
      initResizeDetector();
      updateBlockWidth();
      initFont();
      emit('loaded');
    });

    onBeforeUnmount(() => {
      elementResizeDetector.value?.removeListener(editorContainer.value);
    });

    const handleUpdateInputValue = ({ id: fieldId, value }: { id: string; value: string }) => {
      emit(
        'update-recipient-input',
        props.recipientInput.filter(input => input.fieldId !== fieldId).concat({ fieldId, value })
      );
      editorViewStore.dispatch(`${namespace}/${EditorViewInputAction.UpdateInputValue}`, {
        value,
        fieldId,
        blockId: props.blockId,
      });
    };

    return {
      commentsWithoutSectionId,
      editorContainer,
      blockWidthClass,
      loadFont,
      setBlockWidth,
      handleUpdateInputValue,
      updatingInputs,
      failedInputUpdates,
      firstRowContainsCoverImage,
      lastRowContainsCoverImage,
      sections,
      visibleSections,
      blockOffsetWidth,
      initResizeDetector,
      handleBlockResize,
      elementResizeDetector,
      filterCommentsBySection: CommentsHelper.filterCommentsBySection,
      filteredSections,
    };
  },
});
